import React, { useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import { CampaignData, FilterStatus, StatItem } from './types';

const DISABLED_FILTERS: Set<FilterStatus> = new Set(['opened', 'clicked', 'replied']);

interface CampaignStatsProps {
  campaign: CampaignData;
  filterStatus: FilterStatus;
  onFilterChange: (filter: FilterStatus) => void;
}

export const CampaignStats: React.FC<CampaignStatsProps> = ({
  campaign,
  filterStatus,
  onFilterChange,
}) => {
  const calcRate = (value: number, total: number): number =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  const isDraft = campaign.status === 'draft';

  const stats: StatItem[] = [
    // Show Draft when campaign is in draft status, otherwise show Queued
    ...(isDraft
      ? [{ label: 'Draft', value: campaign.stats.draft, filter: 'draft' as FilterStatus }]
      : [{ label: 'Queued', value: campaign.stats.queued, filter: 'queued' as FilterStatus }]),
    { label: 'Sent', value: campaign.stats.sent, filter: 'sent' as FilterStatus },
    { label: 'Opened', value: campaign.stats.opened, rate: calcRate(campaign.stats.opened, campaign.stats.sent), filter: 'opened' as FilterStatus },
    { label: 'Clicked', value: campaign.stats.clicked, rate: calcRate(campaign.stats.clicked, campaign.stats.sent), filter: 'clicked' as FilterStatus },
    { label: 'Replied', value: campaign.stats.replied, rate: calcRate(campaign.stats.replied, campaign.stats.sent), filter: 'replied' as FilterStatus },
    // Show Bounced only when there are bounced recipients
    ...(campaign.stats.bounced > 0
      ? [{ label: 'Bounced', value: campaign.stats.bounced, rate: calcRate(campaign.stats.bounced, campaign.stats.sent), filter: 'bounced' as FilterStatus }]
      : []),
  ];

  const handleFilterClick = useCallback((filter: FilterStatus) => {
    if (!DISABLED_FILTERS.has(filter)) {
      onFilterChange(filter);
    }
  }, [onFilterChange]);

  return (
    <div className="flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 border-b border-neutral-100 overflow-x-auto scrollbar-hide">
      <div className="flex items-baseline gap-2 sm:gap-4 min-w-max">
        {stats.map((stat, index) => {
          const isDisabled = DISABLED_FILTERS.has(stat.filter);
          return (
            <div key={stat.label} className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => handleFilterClick(stat.filter)}
                disabled={isDisabled}
                className={`text-left px-2 sm:px-3 py-1.5 sm:py-2 -mx-2 sm:-mx-3 rounded-lg transition-colors ${
                  isDisabled
                    ? 'opacity-50 cursor-default'
                    : filterStatus === stat.filter
                      ? 'bg-neutral-100'
                      : 'hover:bg-neutral-50'
                }`}
                title={isDisabled ? 'Coming soon' : undefined}
              >
                <p className={`text-[10px] sm:text-[11px] uppercase tracking-wide ${
                  isDisabled
                    ? 'text-neutral-300'
                    : filterStatus === stat.filter ? 'text-neutral-600' : 'text-neutral-400'
                }`}>
                  {stat.label}
                  {isDisabled && <span className="ml-1 normal-case tracking-normal italic">soon</span>}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className={`text-lg sm:text-xl font-semibold ${
                    isDisabled
                      ? 'text-neutral-300'
                      : filterStatus === stat.filter ? 'text-neutral-900' : 'text-neutral-700'
                  }`}>{stat.value}</span>
                  {stat.rate !== undefined && (
                    <span className={`text-[10px] sm:text-[11px] ${isDisabled ? 'text-neutral-300' : 'text-neutral-400'}`}>{stat.rate}%</span>
                  )}
                </div>
              </button>
              {index < stats.length - 1 && (
                <ChevronRight className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-neutral-300" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
